import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import TeamContext from '@atom/components/teamPortal/TeamContext';
import { CREATE_USER_GROUP, UPDATE_USER_GROUP } from '@atom/graph/userGroup';
import { Button, Icon, Modal, TextField } from '@atom/mui';
import fonts from '@atom/styles/fonts';
import {
  UserGroup,
  UserGroupCreateInput,
  UserGroupUpdateInput,
} from '@atom/types/userGroups';
import { getColorSwatchList } from '@atom/utilities/colorUtilities';

import classes from './teamControls.module.css';

const styles = {
  dialog: {
    bodyStyle: {
      margin: 0,
      padding: 0,
    },
    contentStyle: {
      paddingRight: '2rem',
      paddingBottom: '1rem',
    },
    title: {
      fontSize: fonts.xl,
      padding: '1.125em',
      fontWeight: '500',
    },
    actionsContainerStyle: {
      paddingRight: '1rem',
      paddingBottom: '1rem',
    },
  },
  actionButtons: {
    cancel: { marginRight: '1rem' },
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  groupId: string;
  isEdit?: boolean;
  groupColorId?: number;
  groupName?: string;
}

const CreateEditUserGroupModal = ({
  open,
  onClose,
  isEdit,
  groupId,
  groupColorId,
  groupName,
}: Props) => {
  const { refetchTree, refetchTable } = useContext(TeamContext);
  const [name, setName] = useState<string>(groupName || '');
  const [colorId, setColorId] = useState<number>(groupColorId || 0);
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    if (open) {
      setName(groupName || '');
      setColorId(groupColorId || 0);
    }
  }, [open, groupName, groupColorId]);

  const [createUserGroup, { error: createUserGroupError }] = useMutation<
    { userGroupCreate: UserGroup },
    { input: UserGroupCreateInput }
  >(CREATE_USER_GROUP, {
    onCompleted: () => {
      refetchTree();
      refetchTable();
    },
  });

  useEffect(() => {
    if (createUserGroupError) {
      // @ts-ignore
      const statusCode = createUserGroupError.networkError?.statusCode;

      if (statusCode === 409) {
        setErrorText('This name is already in use.');
      }
    }
  }, [createUserGroupError]);

  const [updateUserGroup, { error: updateUserGroupError }] = useMutation<
    { userGroupUpdate: UserGroup },
    { input: UserGroupUpdateInput }
  >(UPDATE_USER_GROUP, {
    onCompleted: () => {
      refetchTree();
      refetchTable();
    },
  });

  useEffect(() => {
    if (updateUserGroupError) {
      // @ts-ignore
      const statusCode = updateUserGroupError.networkError?.statusCode;

      if (statusCode === 409) {
        setErrorText('This name is already in use.');
      }
    }
  }, [updateUserGroupError]);

  const closeModal = () => {
    onClose();
    setName('');
    setColorId(0);
    setErrorText('');
  };

  const confirm = async () => {
    if (isEdit) {
      await updateUserGroup({
        variables: {
          input: {
            id: groupId,
            ...(name !== groupName && { name }),
            colorId,
          },
        },
      });
    } else {
      await createUserGroup({
        variables: {
          input: {
            parentGroupId: groupId,
            name,
            colorId,
          },
        },
      });
    }

    closeModal();
  };

  const renderColorSwatches = () => {
    const colorSwatch = getColorSwatchList();

    return colorSwatch.map((swatch, index) => (
      <div
        key={index}
        className={clsx(
          classes.colorSwatch,
          index === colorId && classes.selected,
        )}
        onClick={() => setColorId(index)}
      >
        <Icon style={{ fontSize: '30px' }} color={swatch}>
          lens
        </Icon>
      </div>
    ));
  };

  const getFooter = () => {
    return (
      <div style={styles.dialog.actionsContainerStyle}>
        <Button sx={styles.actionButtons.cancel} onClick={closeModal}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={confirm}
          disabled={R.isEmpty(name)}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    );
  };

  return (
    <Modal
      title={isEdit ? 'Edit Group' : 'Create Group'}
      footer={getFooter()}
      contentStyle={styles.dialog.contentStyle}
      style={styles.dialog.bodyStyle}
      open={open}
    >
      <div>
        <TextField
          label="Name"
          value={name}
          name="groupName"
          onChange={event => setName(event.target.value)}
          error={errorText.length > 0}
          helperText={errorText}
        />
        <div className={classes.colorTitle}>Color:</div>
        <div className={classes.colorContainer}>{renderColorSwatches()}</div>
      </div>
    </Modal>
  );
};

export default CreateEditUserGroupModal;
